/* eslint-disable max-len */
import {UploadOutlined} from '@ant-design/icons';
import {Button, Dropdown, Flex, Select} from 'antd';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Editor, Range, Element} from 'slate';

import {allRoutes} from '../../constants/Routes';
import {SaveSource} from '../../pages/reportBuilder/hooks/useReportDocumentSave';

import IconWithLabel from './IconWithLabel';
import SaveStatusIndicator from './SaveStatusIndicator';
import {LineSpacingButton} from './ToolbarComponents';
import ToolbarIcons from './ToolbarIcons';

interface ToolbarProps {
  editor: Editor;
  toggleMark: (format: string) => void;
  toggleBlock: (format: string) => void;
  setAlignment: (align: string) => void;
  setFontSize: (size: number) => void;
  setFontFamily: (font: string) => void;
  toggleColor: (type: string, color: string) => void;
  insertLink: () => void;
  clearFormatting: (editor: Editor) => void;
  isMarkActive: (editor: Editor, format: string) => boolean;
  getActiveBlock: () => string;
  getActiveAlignment: () => string;
  triggerImageUpload: () => void;
  insertShape: (type: string) => void;
  insertTable: () => void;
  insertColumns: (count: number, gap?: number) => void;
  removeColumns: () => void;
  handleToggleList: (format: string) => void;
  startScreenCapture: () => void;
  handleExportPDF: () => void;
  handlePrint: () => void;
  handleExportWord: () => void;
  pageOrientation: 'portrait' | 'landscape';
  setPageOrientation: (orientation: 'portrait' | 'landscape') => void;
  pageMargins: any;
  setPageMargins: (margins: any) => void;
  toolbarExpanded: boolean;
  setToolbarExpanded: (val: boolean) => void;
  openWatermarkModal: (anchorEl?: HTMLElement | null) => void;
  openPageNumberModal: (anchorEl?: HTMLElement | null) => void;
  sidebarOpen: boolean;
  setSidebarOpen: (val: boolean) => void;
  commentPanelOpen: boolean;
  setCommentPanelOpen: (val: boolean) => void;
  insertPageNumber: (settings: any) => void;
  removePageNumbers: () => void;
  insertPageBreak: () => void;
  insertComment: () => void;
  toolbarSync: any;
  indentList: () => void;
  outdentList: () => void;
  handleClearDocument: () => void;
  insertHeader: (anchorEl?: HTMLElement | null) => void;
  insertFooter: (anchorEl?: HTMLElement | null) => void;
  openMarginsModal: (anchorEl?: HTMLElement | null) => void;
  setIsHeaderFooterEditMode: (val: boolean) => void;
  isHeaderFooterEditMode: boolean;
  selectedRanges: Range[];
  saveDocument: (source?: SaveSource) => Promise<void>;
  openColorPicker: (
    type: 'text' | 'highlight' | 'block-bg',
    anchorEl?: HTMLElement | null
  ) => void;
  reportData: any;
  onRenameReport?: (name: string) => void | Promise<void>;
}

/* Tiny icon helper */
const I = ({
  d,
  w = 18,
  h = 18
}: {
  d: string;
  w?: number;
  h?: number;
}): React.ReactElement => (
  <svg
    className="toolbar-icon-base"
    viewBox="0 0 24 24"
    width={w}
    height={h}
    fill="currentColor"
  >
    <path d={d} />
  </svg>
);

const ICONS = {
  back: 'M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z',
  menu: 'M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z',
  download: 'M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z',
  undo: 'M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z',
  redo: 'M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16c1.05-3.19 4.05-5.5 7.6-5.5 1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z',
  indentIncrease:
    'M3 21h18v-2H3v2zM3 8v8l4-4-4-4zm8 9h10v-2H11v2zM3 3v2h18V3H3zm8 6h10V7H11v2zm0 4h10v-2H11v2z',
  indentDecrease:
    'M11 17h10v-2H11v2zm-8-5l4 4V8l-4 4zm0 9h18v-2H3v2zM3 3v2h18V3H3zm8 6h10V7H11v2zm0 4h10v-2H11v2z',
  trash:
    'M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z'
};

/** Ribbon-group with optional bottom label (matches Figma) */
const RibbonGroup = ({
  children,
  label
}: {
  children: React.ReactNode;
  label?: string;
}): React.ReactElement => (
  <div className="ribbon-group">
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2
      }}
    >
      <div style={{display: 'flex', alignItems: 'center', gap: 2}}>
        {children}
      </div>
      {label && (
        <span
          style={{
            fontSize: '10px',
            color: '#5f6368',
            whiteSpace: 'nowrap',
            userSelect: 'none'
          }}
        >
          {label}
        </span>
      )}
    </div>
  </div>
);

export const useToolbarSync = (
  editor: Editor
): {toolbarSync: any; syncToolbar: () => void} => {
  const [toolbarSync, setToolbarSync] = useState<any>({
    fontSize: 12,
    fontFamily: 'Inter',
    color: '#000000',
    highlight: 'transparent',
    bold: false,
    italic: false,
    underline: false,
    align: 'left'
  });

  const sync = useCallback(() => {
    const marks = Editor.marks(editor) ?? {};
    const [block] = Array.from(
      Editor.nodes(editor, {
        match: (n) =>
          !Editor.isEditor(n) &&
          Element.isElement(n) &&
          !['page', 'header-wrapper', 'footer-wrapper'].includes(n.type) &&
          Editor.isBlock(editor, n)
      })
    );

    setToolbarSync({
      fontSize: Number(marks.fontSize) || 12,
      fontFamily: marks.fontFamily || 'Inter',
      color: marks.color || '#000000',
      highlight: marks.highlight || 'transparent',
      bold: !!marks.bold,
      italic: !!marks.italic,
      underline: !!marks.underline,
      strikethrough: !!marks.strikethrough,
      subscript: !!marks.subscript,
      superscript: !!marks.superscript,
      align: (block?.[0] as Element)?.align ?? 'left',
      blockType: (block?.[0] as Element)?.type ?? 'paragraph',
      selectionUpdated: Date.now()
    });
  }, [editor]);

  useEffect(() => {
    sync();
  }, [editor.selection, sync]);

  return {toolbarSync, syncToolbar: sync};
};

export const Toolbar = (props: ToolbarProps): React.ReactElement => {
  const navigate = useNavigate();
  const {
    editor,
    toggleMark,
    toggleBlock,
    sidebarOpen,
    setSidebarOpen,
    setAlignment,
    setFontSize,
    setFontFamily,
    insertLink,
    clearFormatting,
    triggerImageUpload,
    insertTable,
    insertColumns,
    handleToggleList,
    startScreenCapture,
    handleExportPDF,
    handleExportWord,
    handlePrint,
    pageOrientation,
    setPageOrientation,
    openWatermarkModal,
    openPageNumberModal,
    insertPageBreak,
    insertComment,
    toolbarSync,
    indentList,
    outdentList,
    saveDocument,
    insertHeader,
    insertFooter,
    openMarginsModal,
    // setIsHeaderFooterEditMode,
    // isHeaderFooterEditMode,
    handleClearDocument,
    openColorPicker,
    reportData,
    onRenameReport
    // pageMargins
  } = props;

  const [localTitle, setLocalTitle] = useState(
    (reportData?.filename as string) ?? ''
  );
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [activeTab, setActiveTab] = useState('Home');
  const [activePopup, setActivePopup] = useState<'columns' | 'shapes' | null>(
    null
  );
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      !isEditingTitle &&
      reportData?.filename &&
      reportData.filename !== localTitle
    ) {
      setLocalTitle(reportData.filename);
    }
  }, [reportData?.filename, isEditingTitle, localTitle]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        setActivePopup(null);
      }
    };

    if (activePopup) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activePopup]);

  const selectFontFamilyRef = useRef<HTMLSelectElement>(null);
  useEffect(() => {
    if (toolbarSync?.fontFamily && selectFontFamilyRef.current) {
      selectFontFamilyRef.current.value = toolbarSync.fontFamily as string;
    }
  }, [toolbarSync?.fontFamily, toolbarSync?.selectionUpdated]);

  const [fontSize, setLocalFontSize] = useState(12);
  useEffect(() => {
    if (toolbarSync) {
      setLocalFontSize(toolbarSync.fontSize as number);
    }
  }, [toolbarSync?.fontSize, toolbarSync?.selectionUpdated]);

  const tb = (
    active?: boolean,
    variant?: 'blockType' | 'sidebarToggle'
  ): string =>
    `toolbar-btn${
      variant === 'blockType'
        ? ' toolbar-btn--blocktype'
        : variant === 'sidebarToggle'
          ? ' toolbar-btn--sidebar-toggle'
          : ''
    }${active ? ' active' : ''}`;

  const blockType = (toolbarSync?.blockType as string) ?? 'paragraph';
  // const displayTitle = localTitle.trim() || 'Untitled Document';
  const saveTitle = async (): Promise<void> => {
    await onRenameReport?.(localTitle);
    setIsEditingTitle(false);
  };
  const orientationMenuItems = [
    {
      key: 'portrait',
      label: 'Portrait',
      onClick: () => {
        setPageOrientation('portrait');
      }
    },
    {
      key: 'landscape',
      label: 'Landscape',
      onClick: () => {
        setPageOrientation('landscape');
      }
    }
  ];
  const canUndo = (editor as any).history?.undos?.length > 0;
  const canRedo = (editor as any).history?.redos?.length > 0;

  const undoRedoGroup = (
    <>
      <Button
        className="toolbar-btn"
        onClick={() => {
          (editor as any).undo?.();
        }}
        title="Undo"
        disabled={!canUndo}
      >
        <ToolbarIcons tool="undo" disabled={!canUndo} />
      </Button>
      <Button
        className="toolbar-btn"
        onClick={() => {
          (editor as any).redo?.();
        }}
        title="Redo"
        disabled={!canRedo}
      >
        <ToolbarIcons tool="redo" disabled={!canRedo} />
      </Button>
    </>
  );

  return (
    <div className="toolbar-wrapper editor">
      <div className="toolbar-ribbon">
        {/* ── Top Header Row ── */}
        <div className="ribbon-row top">
          <div className="ribbon-group ribbon-group--document-title">
            <Button
              className="toolbar-btn"
              onClick={() => {
                navigate(allRoutes.reportStudio.reportBuilder);
              }}
              title="Back to Documents"
            >
              <I d={ICONS.back} w={20} h={20} />
            </Button>

            {isEditingTitle ? (
              <input
                type="text"
                className="document-title-input"
                value={localTitle}
                autoFocus
                onChange={(e) => {
                  setLocalTitle(e.target.value);
                }}
                onBlur={() => {
                  void saveTitle();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    void saveTitle();
                  }
                  if (e.key === 'Escape') {
                    setIsEditingTitle(false);
                    setLocalTitle((reportData?.filename as string) ?? '');
                  }
                }}
                style={{
                  border: '1px solid #c9ccd3',
                  borderRadius: '4px',
                  background: '#ffffff',
                  fontSize: '18px',
                  fontWeight: 500,
                  outline: 'none',
                  padding: '8px 10px',
                  minWidth: '220px'
                }}
              />
            ) : (
              <div className="toolbar-document-title">
                <span
                  className="toolbar-document-title__text"
                  onClick={() => {
                    setIsEditingTitle(true);
                  }}
                >
                  {reportData?.filename}
                </span>
              </div>
            )}
            <SaveStatusIndicator />
          </div>
          <div
            className="ribbon-group"
            style={{marginLeft: 'auto', display: 'flex', gap: '8px'}}
          >
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'pdf',
                    label: 'Export as PDF',
                    onClick: () => {
                      handleExportPDF();
                    }
                  },
                  {
                    key: 'word',
                    label: 'Export as Word (.docx)',
                    onClick: () => {
                      handleExportWord();
                    }
                  }
                ]
              }}
              trigger={['click']}
            >
              <Button
                type="primary"
                icon={<UploadOutlined />}
                style={{background: '#3e5c3a'}}
              >
                Export
              </Button>
            </Dropdown>
          </div>
        </div>

        {/* ── Tab Row ── */}
        <div
          className="ribbon-tabs"
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '6px 12px',
            borderBottom: '1px solid #d2d4da',
            borderTop: '1px solid #d2d4da',
            background: '#fff'
          }}
        >
          <div className="ribbon-tabs-left">
            <Button
              className="toolbar-btn toolbar-btn--sidebar-toggle"
              onClick={() => {
                setSidebarOpen(!sidebarOpen);
              }}
              title="Toggle Sidebar"
            >
              <I d={ICONS.menu} w={20} h={20} />
              <span style={{marginLeft: '6px'}}>
                {sidebarOpen ? 'Hide Questions' : 'Show Questions'}
              </span>
            </Button>
            <Button
              className="toolbar-btn"
              onClick={() => {
                void saveDocument('manual');
              }}
              title="Save Document"
            >
              <ToolbarIcons tool="save" />
            </Button>
            <Button
              className="toolbar-btn"
              onClick={() => {
                handlePrint();
              }}
              title="Print Document"
            >
              <ToolbarIcons tool="print" />
            </Button>
            {undoRedoGroup}
          </div>
          <div className="ribbon-tabs-center">
            {['Home', 'Insert', 'Page Layout'].map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  setActiveTab(tab);
                }}
                style={{
                  padding: '4px 2px',
                  border: 'none',
                  background: 'transparent',
                  color: activeTab === tab ? '#435B37' : '#404252',
                  borderBottom:
                    activeTab === tab
                      ? '2px solid #435B37'
                      : '2px solid transparent',
                  fontWeight: activeTab === tab ? '600' : 'normal',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                {tab}
              </button>
            ))}
          </div>
          <div className="ribbon-tabs-right">
            <Button
              className={tb(props.commentPanelOpen)}
              onClick={() => {
                props.setCommentPanelOpen(!props.commentPanelOpen);
              }}
              title="Toggle Comments"
            >
              <ToolbarIcons tool="comment" />
              <span style={{marginLeft: '6px'}}>Comment</span>
            </Button>
          </div>
        </div>

        {/* ── Ribbon Action Row ── */}
        <div className="ribbon-row bottom">
          {/* ═══ HOME TAB ═══ */}
          {activeTab === 'Home' && (
            <>
              {/* Undo / Redo */}

              <Flex vertical className="ribbon-box">
                {/* Font Family + Size */}
                <RibbonGroup>
                  <Select
                    value={(toolbarSync?.fontFamily as string) ?? 'Inter'}
                    onChange={(value) => {
                      setFontFamily(value);
                    }}
                    style={{width: '120px'}}
                    options={[
                      {value: 'Inter', label: 'Inter'},
                      {value: 'Montserrat', label: 'Montserrat'},
                      {value: 'Roboto', label: 'Roboto'},
                      {value: 'Arial', label: 'Arial'},
                      {value: 'Times New Roman', label: 'Times New Roman'}
                    ]}
                  />
                  <div className="font-size-wrapper">
                    <button
                      type="button"
                      aria-label="Decrease font size"
                      onClick={() => {
                        setFontSize(Math.max(1, fontSize - 1));
                      }}
                    >
                      -
                    </button>
                    <input
                      type="number"
                      value={fontSize}
                      onChange={(e) => {
                        setFontSize(parseInt(e.target.value) || 12);
                      }}
                    />
                    <button
                      type="button"
                      aria-label="Increase font size"
                      onClick={() => {
                        setFontSize(fontSize + 1);
                      }}
                    >
                      +
                    </button>
                  </div>
                  <Button
                    className="toolbar-btn"
                    onClick={() => {
                      clearFormatting(editor);
                    }}
                    title="Clear Formatting"
                  >
                    <ToolbarIcons tool="clearFormatting" />
                  </Button>
                  <Button
                    className="toolbar-btn"
                    onClick={handleClearDocument}
                    title="Clear All Content"
                    style={{color: '#d93025'}}
                  >
                    <ToolbarIcons tool="trash" />
                  </Button>
                </RibbonGroup>

                {/* B / I / U / S / Sub / Sup */}
                <RibbonGroup>
                  <Button
                    className={tb(!!toolbarSync?.bold)}
                    onClick={() => {
                      toggleMark('bold');
                    }}
                    title="Bold"
                  >
                    <ToolbarIcons tool="bold" />
                  </Button>
                  <Button
                    className={tb(!!toolbarSync?.italic)}
                    onClick={() => {
                      toggleMark('italic');
                    }}
                    title="Italic"
                  >
                    <ToolbarIcons tool="italic" />
                  </Button>
                  <Button
                    className={tb(!!toolbarSync?.underline)}
                    onClick={() => {
                      toggleMark('underline');
                    }}
                    title="Underline"
                  >
                    <ToolbarIcons tool="underline" />
                  </Button>
                  <Button
                    className={tb(!!toolbarSync?.strikethrough)}
                    onClick={() => {
                      toggleMark('strikethrough');
                    }}
                    title="Strikethrough"
                  >
                    <ToolbarIcons tool="strikeThrough" />
                  </Button>
                  <Button
                    className={tb(!!toolbarSync?.subscript)}
                    onClick={() => {
                      toggleMark('subscript');
                    }}
                    title="Subscript"
                  >
                    <ToolbarIcons tool="subScript" />
                  </Button>
                  <Button
                    className={tb(!!toolbarSync?.superscript)}
                    onClick={() => {
                      toggleMark('superscript');
                    }}
                    title="Superscript"
                  >
                    <ToolbarIcons tool="superScript" />
                  </Button>
                  <Button
                    className="toolbar-btn"
                    onClick={(e) => {
                      openColorPicker('highlight', e.currentTarget);
                    }}
                    title="Highlight Color"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '2px'
                    }}
                  >
                    <ToolbarIcons
                      tool="highlight"
                      strokeColor={toolbarSync?.highlight ?? '#000'}
                    />
                  </Button>
                  <Button
                    className="toolbar-btn"
                    onClick={(e) => {
                      openColorPicker('text', e.currentTarget);
                    }}
                    title="Text Color"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '2px'
                    }}
                  >
                    <ToolbarIcons
                      tool="textColor"
                      strokeColor={toolbarSync?.color ?? '#000'}
                    />
                  </Button>
                </RibbonGroup>
              </Flex>

              <Flex vertical className="ribbon-box">
                <Flex vertical={false} gap={8}>
                  {/* Lists + Indent/Outdent */}
                  <RibbonGroup>
                    <Button
                      className="toolbar-btn"
                      onClick={() => {
                        handleToggleList('bulleted-list');
                      }}
                      title="Bulleted List"
                    >
                      <ToolbarIcons tool="bulletList" />
                    </Button>
                    <Button
                      className="toolbar-btn"
                      onClick={() => {
                        handleToggleList('numbered-list');
                      }}
                      title="Numbered List"
                    >
                      <ToolbarIcons tool="numberedList" />
                    </Button>
                  </RibbonGroup>
                  <RibbonGroup>
                    <LineSpacingButton
                      editor={editor}
                      selectedRanges={props.selectedRanges}
                    />
                    <Button
                      className="toolbar-btn"
                      onClick={() => {
                        outdentList();
                      }}
                      title="Decrease Indent"
                    >
                      <ToolbarIcons tool="decreaseIndent" />
                    </Button>
                    <Button
                      className="toolbar-btn"
                      onClick={() => {
                        indentList();
                      }}
                      title="Increase Indent"
                    >
                      <ToolbarIcons tool="increaseIndent" />
                    </Button>
                  </RibbonGroup>
                </Flex>
                {/* Alignment */}
                <RibbonGroup>
                  <Button
                    className={tb(toolbarSync?.align === 'left')}
                    onClick={() => {
                      setAlignment('left');
                    }}
                    title="Align Left"
                  >
                    <ToolbarIcons tool="left" />
                  </Button>
                  <Button
                    className={tb(toolbarSync?.align === 'center')}
                    onClick={() => {
                      setAlignment('center');
                    }}
                    title="Align Center"
                  >
                    <ToolbarIcons tool="center" />
                  </Button>
                  <Button
                    className={tb(toolbarSync?.align === 'right')}
                    onClick={() => {
                      setAlignment('right');
                    }}
                    title="Align Right"
                  >
                    <ToolbarIcons tool="right" />
                  </Button>
                  <Button
                    className={tb(toolbarSync?.align === 'justify')}
                    onClick={() => {
                      setAlignment('justify');
                    }}
                    title="Justify"
                  >
                    <ToolbarIcons tool="justify" />
                  </Button>
                </RibbonGroup>
              </Flex>

              {/* Heading Styles */}
              <RibbonGroup>
                <Button
                  className={tb(blockType === 'paragraph', 'blockType')}
                  onClick={() => {
                    toggleBlock('paragraph');
                  }}
                  style={{
                    fontSize: '12px',
                    padding: '4px 10px',
                    minWidth: 'auto'
                  }}
                >
                  Normal
                </Button>
                <Button
                  className={tb(blockType === 'heading-one', 'blockType')}
                  onClick={() => {
                    toggleBlock('heading-one');
                  }}
                  style={{
                    fontSize: '14px',
                    fontWeight: 700,
                    padding: '4px 10px',
                    minWidth: 'auto'
                  }}
                >
                  Heading 1
                </Button>
                <Button
                  className={tb(blockType === 'heading-two', 'blockType')}
                  onClick={() => {
                    toggleBlock('heading-two');
                  }}
                  style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    padding: '4px 10px',
                    minWidth: 'auto'
                  }}
                >
                  Heading 2
                </Button>
                <Button
                  className={tb(blockType === 'heading-three', 'blockType')}
                  onClick={() => {
                    toggleBlock('heading-three');
                  }}
                  style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    padding: '4px 10px',
                    minWidth: 'auto'
                  }}
                >
                  Heading 3
                </Button>
              </RibbonGroup>
            </>
          )}

          {/* ═══ INSERT TAB ═══ */}
          {activeTab === 'Insert' && (
            <>
              {/* <RibbonGroup label="Breaks">
                <Button
                  className="toolbar-btn"
                  onClick={() => {
                    insertPageBreak();
                  }}
                  title="Page Break"
                >
                  <ToolbarIcons tool="pageBreak" />
                </Button>
              </RibbonGroup>

              <RibbonGroup label="Page Number">
                <Button
                  className="toolbar-btn"
                  onClick={() => {
                    openPageNumberModal();
                  }}
                  title="Page Number"
                >
                  <I d={ICONS.pageNumber} />
                </Button>
              </RibbonGroup> */}

              {/* <RibbonGroup label="Header & Footer">
                <Button
                  className={tb(isHeaderFooterEditMode)}
                  onClick={() => {
                    setIsHeaderFooterEditMode(!isHeaderFooterEditMode);
                  }}
                  title="Edit Header/Footer"
                >
                  <I d={ICONS.headerFooter} />
                </Button>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2
                  }}
                >
                  <Button
                    className="toolbar-btn-small"
                    onClick={() => {
                      insertHeader();
                    }}
                  >
                    + Header
                  </Button>
                  <Button
                    className="toolbar-btn-small"
                    onClick={() => {
                      insertFooter();
                    }}
                  >
                    + Footer
                  </Button>
                </div>
              </RibbonGroup> */}

              <RibbonGroup>
                <Button
                  className="toolbar-btn toolbar-btn--labeled"
                  onClick={() => {
                    insertTable();
                  }}
                  title="Table"
                >
                  <IconWithLabel
                    icon={<ToolbarIcons tool="table" />}
                    title="Table"
                  />
                </Button>
                <Button
                  className="toolbar-btn toolbar-btn--labeled"
                  onClick={() => {
                    triggerImageUpload();
                  }}
                  title="Pictures"
                >
                  <IconWithLabel
                    icon={<ToolbarIcons tool="image" />}
                    title="Pictures"
                  />
                </Button>
                <Button
                  className="toolbar-btn toolbar-btn--labeled"
                  onClick={() => {
                    startScreenCapture();
                  }}
                  title="Screenshot"
                >
                  <IconWithLabel
                    icon={<ToolbarIcons tool="screenshot" />}
                    title="Screenshot"
                  />
                </Button>
                <div
                  ref={popupRef}
                  style={{position: 'relative', display: 'inline-flex'}}
                >
                  <Button
                    className="toolbar-btn toolbar-btn--labeled"
                    onClick={() => {
                      setActivePopup((prev) =>
                        prev === 'shapes' ? null : 'shapes'
                      );
                    }}
                    title="Shapes"
                  >
                    <IconWithLabel
                      icon={<ToolbarIcons tool="shapes" />}
                      title="Shapes"
                    />
                  </Button>

                  {activePopup === 'shapes' && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 6px)',
                        left: 0,
                        zIndex: 3000,
                        background: '#fff',
                        border: '1px solid #d9d9d9',
                        borderRadius: 6,
                        boxShadow: '0 8px 18px rgba(0, 0, 0, 0.12)',
                        padding: 8,
                        display: 'flex',
                        flexDirection: 'column', // List them vertically
                        gap: 6,
                        minWidth: '120px'
                      }}
                    >
                      {[
                        'logo',
                        'icon',
                        'banner',
                        'hero',
                        'thumbnail',
                        'circle'
                      ].map((type) => (
                        <Button
                          key={type}
                          className="toolbar-btn-small"
                          onClick={() => {
                            props.insertShape(type);
                            setActivePopup(null);
                          }}
                          style={{
                            textAlign: 'left',
                            textTransform: 'capitalize',
                            width: '100%'
                          }}
                        >
                          {type}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              </RibbonGroup>

              {/* <RibbonGroup label="Columns">
                <Button
                  className="toolbar-btn"
                  onClick={() => {
                    insertColumns(2);
                  }}
                  title="2 Columns"
                >
                  <I d={ICONS.col2} />
                </Button>
                <Button
                  className="toolbar-btn"
                  onClick={() => {
                    insertColumns(3);
                  }}
                  title="3 Columns"
                >
                  <I d={ICONS.col3} />
                </Button>
              </RibbonGroup> */}

              <RibbonGroup>
                <Button
                  className="toolbar-btn toolbar-btn--labeled"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    insertComment();
                  }}
                  title="Comment"
                >
                  <IconWithLabel
                    icon={<ToolbarIcons tool="comment" />}
                    title="Comment"
                  />
                </Button>
                <Button
                  className="toolbar-btn toolbar-btn--labeled"
                  onClick={() => {
                    insertLink();
                  }}
                  title="Link"
                >
                  <IconWithLabel
                    icon={<ToolbarIcons tool="link" />}
                    title="Link"
                  />
                </Button>
              </RibbonGroup>
            </>
          )}

          {/* ═══ PAGE LAYOUT TAB ═══ */}
          {activeTab === 'Page Layout' && (
            <>
              <Button
                className="toolbar-btn toolbar-btn--labeled"
                onClick={(e) => {
                  openMarginsModal(e.currentTarget);
                }}
                title="Margins"
              >
                <IconWithLabel
                  icon={<ToolbarIcons tool="margins" />}
                  title="Margins"
                />
              </Button>

              <Dropdown
                menu={{
                  items: orientationMenuItems,
                  selectable: true,
                  selectedKeys: [pageOrientation]
                }}
                trigger={['click']}
              >
                <Button
                  className="toolbar-btn toolbar-btn--labeled"
                  title="Orientation"
                >
                  <IconWithLabel
                    icon={<ToolbarIcons tool="orientation" />}
                    title="Orientation"
                  />
                </Button>
              </Dropdown>

              {/* <RibbonGroup label="Columns">
                <Button
                  className="toolbar-btn"
                  onClick={() => {
                    insertColumns(2);
                  }}
                  title="2 Columns"
                >
                  <ToolbarIcons tool="columns" />
                </Button>
                <Button
                  className="toolbar-btn"
                  onClick={() => {
                    insertColumns(3);
                  }}
                  title="3 Columns"
                >
                  <ToolbarIcons tool="columns" />
                </Button>
              </RibbonGroup> */}
              <div
                ref={popupRef}
                style={{position: 'relative', display: 'inline-flex'}}
              >
                <Button
                  className="toolbar-btn toolbar-btn--labeled"
                  onClick={() => {
                    setActivePopup((prev) =>
                      prev === 'columns' ? null : 'columns'
                    );
                  }}
                  title="Columns"
                >
                  <IconWithLabel
                    icon={<ToolbarIcons tool="columns" />}
                    title="Columns"
                  />
                </Button>
                {activePopup === 'columns' && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 6px)',
                      left: 0,
                      zIndex: 3000,
                      background: '#fff',
                      border: '1px solid #d9d9d9',
                      borderRadius: 6,
                      boxShadow: '0 8px 18px rgba(0, 0, 0, 0.12)',
                      padding: 8,
                      display: 'flex',
                      gap: 6
                    }}
                  >
                    {[2, 3, 4].map((count) => (
                      <Button
                        key={count}
                        className="toolbar-btn-small"
                        onClick={() => {
                          insertColumns(count);
                          setActivePopup(null);
                        }}
                        title={`${count} Columns`}
                      >
                        {count}
                      </Button>
                    ))}
                    <Button
                      className="toolbar-btn-small"
                      onClick={() => {
                        props.removeColumns();
                        setActivePopup(null);
                      }}
                      title="Remove Columns"
                    >
                      Remove
                    </Button>
                  </div>
                )}
              </div>

              <RibbonGroup>
                <Button
                  className="toolbar-btn toolbar-btn--labeled"
                  onClick={(e) => {
                    openWatermarkModal(e.currentTarget);
                  }}
                  title="Watermark"
                >
                  <IconWithLabel
                    icon={<ToolbarIcons tool="watermark" />}
                    title="Watermark"
                  />
                </Button>
              </RibbonGroup>

              <RibbonGroup>
                <Button
                  className="toolbar-btn toolbar-btn--labeled"
                  onClick={() => {
                    insertPageBreak();
                  }}
                  title="Page Break"
                >
                  <IconWithLabel
                    icon={<ToolbarIcons tool="pageBreak" />}
                    title="Page Break"
                  />
                </Button>
              </RibbonGroup>

              <RibbonGroup>
                <Button
                  className="toolbar-btn toolbar-btn--labeled"
                  onClick={(e) => {
                    insertHeader(e.currentTarget);
                  }}
                  title="Insert Header"
                >
                  <IconWithLabel
                    icon={<ToolbarIcons tool="header" />}
                    title="Header"
                  />
                </Button>
                <Button
                  className="toolbar-btn toolbar-btn--labeled"
                  onClick={(e) => {
                    insertFooter(e.currentTarget);
                  }}
                  title="Insert Footer"
                >
                  <IconWithLabel
                    icon={<ToolbarIcons tool="footer" />}
                    title="Footer"
                  />
                </Button>
              </RibbonGroup>

              <RibbonGroup>
                <Button
                  className="toolbar-btn toolbar-btn--labeled"
                  onClick={(e) => {
                    openPageNumberModal(e.currentTarget);
                  }}
                  title="Page Number"
                >
                  <IconWithLabel
                    icon={<ToolbarIcons tool="pageNumber" />}
                    title="Page Number"
                  />
                </Button>
              </RibbonGroup>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
